import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogTrigger, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { CheckCircle2, XCircle, Clock, Building2, Trophy, User, Eye, MessageCircle, Download } from "lucide-react";
import { toast } from "sonner";

const DIVISIONS = [
  { key: "state", label: "State", eligible: true },
  { key: "district", label: "District", eligible: true },
  { key: "city", label: "City", eligible: true },
  { key: "national", label: "National", eligible: false },
  { key: "international", label: "International", eligible: false },
];

const TIERS = [
  { key: "1", label: "Tier 1 (500 winner pts)" },
  { key: "2", label: "Tier 2 (1250 winner pts)" },
  { key: "3", label: "Tier 3 (2000 winner pts)" },
];

function AadhaarViewer({ front, back, playerName }) {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="outline" className="rounded-sm text-xs uppercase tracking-widest font-bold">
          <Eye className="w-4 h-4 mr-2" /> View Aadhaar
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl rounded-sm p-0 overflow-hidden">
        <div className="bg-jpa-ink text-white px-6 py-4">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl tracking-wider text-white">Aadhaar · {playerName}</DialogTitle>
          </DialogHeader>
        </div>
        <div className="p-6 grid md:grid-cols-2 gap-4 max-h-[80vh] overflow-y-auto">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-widest text-jpa-primary mb-2">Front</div>
            {front ? <img src={front} alt="Aadhaar front" className="w-full border border-jpa-border" /> : <div className="p-6 border border-dashed border-jpa-border text-jpa-muted text-sm">Not uploaded</div>}
          </div>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-widest text-jpa-primary mb-2">Back</div>
            {back ? <img src={back} alt="Aadhaar back" className="w-full border border-jpa-border" /> : <div className="p-6 border border-dashed border-jpa-border text-jpa-muted text-sm">Not uploaded</div>}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function PlayerApprovalCard({ player, onDone }) {
  const [deleteAadhaar, setDeleteAadhaar] = useState(true);
  const [approvedInfo, setApprovedInfo] = useState(null); // { jpa_id, phone, card_url }
  const [busy, setBusy] = useState(false);

  const approve = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/admin/players/${player.id}/approve`, { delete_aadhaar: deleteAadhaar });
      toast.success(`${player.name} approved — JPA ID: ${r.data.jpa_id}`);
      setApprovedInfo({ jpa_id: r.data.jpa_id, phone: r.data.phone || player.phone, card_url: r.data.card_url });
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const reject = async () => {
    try {
      await api.post(`/admin/players/${player.id}/reject`);
      toast.success(`${player.name} rejected`);
      onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const openWhatsApp = () => {
    if (!approvedInfo?.card_url) return;
    const digits = String(approvedInfo.phone || "").replace(/[^0-9]/g, "");
    // Assume Indian numbers if 10 digits — prefix 91
    const waNumber = digits.length === 10 ? `91${digits}` : digits;
    const msg = `Your Registration with Jharkhand Pickleball Association is approved. Kindly Download Your Card From the Following Link: ${approvedInfo.card_url}`;
    const url = waNumber
      ? `https://wa.me/${waNumber}?text=${encodeURIComponent(msg)}`
      : `https://wa.me/?text=${encodeURIComponent(msg)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const dob = player.dob ? new Date(player.dob).toLocaleDateString("en-IN") : "—";
  return (
    <div data-testid={`player-approval-${player.id}`} className="border border-jpa-border rounded-sm p-5">
      <div className="flex items-start gap-4">
        <div className="w-20 h-20 rounded-sm overflow-hidden bg-jpa-surface flex-shrink-0">
          {player.photo_data_url && <img src={player.photo_data_url} alt="" className="w-full h-full object-cover" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-display text-xl tracking-wider">{player.name}</div>
            <Badge variant="outline" className="rounded-sm text-[10px] uppercase tracking-widest">{player.district}</Badge>
            <Badge variant="outline" className="rounded-sm text-[10px] uppercase tracking-widest">{player.gender === "M" ? "Male" : player.gender === "F" ? "Female" : "Other"}</Badge>
            {approvedInfo && <Badge className="bg-emerald-600 text-white rounded-sm text-[10px] uppercase tracking-widest">Approved · {approvedInfo.jpa_id}</Badge>}
          </div>
          <div className="mt-1 text-sm text-jpa-muted space-y-0.5">
            <div>Email: {player.email} · Phone: {player.phone}</div>
            <div>DOB: <b>{dob}</b>{player.dupr_id ? ` · DUPR: ${player.dupr_id}` : ""}{player.pgid ? ` · PGID: ${player.pgid}` : ""}</div>
          </div>
        </div>
      </div>

      {!approvedInfo && (
        <div className="mt-4 flex items-center gap-2 text-xs text-jpa-muted">
          <input
            id={`del-aadhaar-${player.id}`}
            type="checkbox"
            checked={deleteAadhaar}
            onChange={(e) => setDeleteAadhaar(e.target.checked)}
            data-testid={`player-delete-aadhaar-${player.id}`}
            className="w-4 h-4 accent-jpa-primary cursor-pointer"
          />
          <label htmlFor={`del-aadhaar-${player.id}`} className="cursor-pointer select-none">
            Auto-delete Aadhaar files after approval <span className="text-jpa-primary font-bold">(recommended for PII safety)</span>
          </label>
        </div>
      )}

      <div className="mt-4 flex flex-wrap items-center justify-end gap-2">
        {!approvedInfo ? (
          <>
            <AadhaarViewer front={player.aadhaar_front_data_url} back={player.aadhaar_back_data_url} playerName={player.name} />
            <Button data-testid={`player-reject-${player.id}`} onClick={reject} variant="outline" className="rounded-sm uppercase tracking-widest text-xs font-bold">
              <XCircle className="w-4 h-4 mr-2" /> Reject
            </Button>
            <Button data-testid={`player-approve-${player.id}`} disabled={busy} onClick={approve} className="bg-jpa-primary hover:bg-jpa-primary-hover text-white rounded-sm uppercase tracking-widest text-xs font-bold">
              <CheckCircle2 className="w-4 h-4 mr-2" /> {busy ? "Approving…" : "Approve & issue JPA ID"}
            </Button>
          </>
        ) : (
          <>
            <a
              href={approvedInfo.card_url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`player-preview-card-${player.id}`}
              className="inline-flex items-center gap-2 px-3 py-2 border border-jpa-border rounded-sm text-xs uppercase tracking-widest font-bold hover:border-jpa-primary"
            >
              <Download className="w-4 h-4" /> Preview / download card
            </a>
            <Button
              data-testid={`player-whatsapp-${player.id}`}
              onClick={openWhatsApp}
              className="bg-[#25D366] hover:bg-[#1ebe57] text-white rounded-sm uppercase tracking-widest text-xs font-bold"
            >
              <MessageCircle className="w-4 h-4 mr-2" /> Send WhatsApp
            </Button>
            <Button
              data-testid={`player-done-${player.id}`}
              variant="outline"
              onClick={onDone}
              className="rounded-sm uppercase tracking-widest text-xs font-bold"
            >
              Done
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

function ClubApprovalCard({ club, onDone }) {
  const approve = async () => {
    try {
      await api.post(`/admin/clubs/${club.id}/approve`, { courts: club.courts || 2 });
      toast.success(`${club.name} approved. Club can now log in.`);
      onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const reject = async () => {
    try {
      await api.post(`/admin/clubs/${club.id}/reject`);
      toast.success(`${club.name} rejected.`);
      onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <div data-testid={`club-approval-${club.id}`} className="border border-jpa-border rounded-sm p-5">
      <div className="flex items-start gap-4">
        <div className="w-16 h-16 rounded-sm overflow-hidden bg-jpa-surface flex-shrink-0">
          {club.logo_data_url && <img src={club.logo_data_url} alt="" className="w-full h-full object-cover" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-display text-xl tracking-wider">{club.name}</div>
            <Badge variant="outline" className="rounded-sm text-[10px] uppercase tracking-widest">{club.district}</Badge>
          </div>
          <div className="mt-1 text-sm text-jpa-muted">
            <div>Contact: <b>{club.contact_person}</b> · {club.phone}</div>
            <div>Email: {club.email}</div>
            <div>Address: {club.address}</div>
          </div>
          {club.court_photos_data_urls?.length > 0 && (
            <div className="mt-3 grid grid-cols-4 md:grid-cols-6 gap-2">
              {club.court_photos_data_urls.map((p, i) => (
                <div key={i} className="aspect-square border border-jpa-border">
                  <img src={p} alt="" className="w-full h-full object-cover" />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      <div className="mt-4 flex justify-end gap-2">
        <Button data-testid={`club-reject-${club.id}`} onClick={reject} variant="outline" className="rounded-sm uppercase tracking-widest text-xs font-bold">
          <XCircle className="w-4 h-4 mr-2" /> Reject
        </Button>
        <Button data-testid={`club-approve-${club.id}`} onClick={approve} className="bg-jpa-primary hover:bg-jpa-primary-hover text-white rounded-sm uppercase tracking-widest text-xs font-bold">
          <CheckCircle2 className="w-4 h-4 mr-2" /> Approve
        </Button>
      </div>
    </div>
  );
}

function TournamentApprovalCard({ tournament, onDone }) {
  const [division, setDivision] = useState("state");
  const [tier, setTier] = useState("2");
  const [annual, setAnnual] = useState("false");
  const divisionMeta = DIVISIONS.find((d) => d.key === division);
  const eligible = divisionMeta?.eligible;

  const approve = async () => {
    try {
      await api.post(`/admin/tournaments/${tournament.id}/approve`, {
        division,
        tier: eligible ? parseInt(tier, 10) : null,
        is_annual_ranking: eligible && annual === "true",
        registration_open: true,
        status: "upcoming",
      });
      toast.success(`${tournament.name} approved as ${division.toUpperCase()}${eligible ? ` · Tier ${tier}` : ""}`);
      onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const reject = async () => {
    try {
      await api.post(`/admin/tournaments/${tournament.id}/reject`);
      toast.success(`${tournament.name} marked NOT SANCTIONED`);
      onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div data-testid={`tournament-approval-${tournament.id}`} className="border border-jpa-border rounded-sm p-5">
      <div className="flex items-start gap-4">
        <div className="w-20 h-20 rounded-sm overflow-hidden bg-jpa-surface flex-shrink-0">
          {tournament.image_url && <img src={tournament.image_url} alt="" className="w-full h-full object-cover" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display text-xl tracking-wider">{tournament.name}</div>
          <div className="text-sm text-jpa-muted mt-1">
            <div>{tournament.location} · {tournament.start_date} → {tournament.end_date}</div>
            <div>Organiser: <b>{tournament.organizer_club_name || (tournament.organizer_type === "jpa" ? "JPA" : "—")}</b></div>
            <div>Events: {(tournament.event_categories || []).join(", ") || "—"}</div>
            <div>Age categories: {(tournament.age_categories || []).join(", ") || "—"}</div>
            {tournament.prize_pool && <div>Prize pool: {tournament.prize_pool}</div>}
            {tournament.description && <div className="mt-1">{tournament.description}</div>}
            {tournament.factsheet_data_url && (
              <a href={tournament.factsheet_data_url} download={`${tournament.name.replace(/\s+/g, "_")}_factsheet.pdf`} className="mt-1 inline-block text-jpa-primary font-bold underline">Download factsheet PDF</a>
            )}
          </div>
        </div>
      </div>

      <div className="mt-4 grid md:grid-cols-3 gap-3">
        <div>
          <Label>Division</Label>
          <Select value={division} onValueChange={setDivision}>
            <SelectTrigger data-testid={`ta-division-${tournament.id}`} className="mt-1 rounded-sm"><SelectValue /></SelectTrigger>
            <SelectContent>{DIVISIONS.map((d) => <SelectItem key={d.key} value={d.key}>{d.label}{d.eligible ? " · counts for ranking" : " · no ranking impact"}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label>Tier</Label>
          <Select value={tier} onValueChange={setTier} disabled={!eligible}>
            <SelectTrigger data-testid={`ta-tier-${tournament.id}`} className="mt-1 rounded-sm"><SelectValue /></SelectTrigger>
            <SelectContent>{TIERS.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}</SelectContent>
          </Select>
          {!eligible && <div className="text-[10px] text-jpa-muted mt-1">Tier not applicable for {division}.</div>}
        </div>
        <div>
          <Label>Annual Ranking Tournament?</Label>
          <Select value={annual} onValueChange={setAnnual} disabled={!eligible}>
            <SelectTrigger data-testid={`ta-annual-${tournament.id}`} className="mt-1 rounded-sm"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="false">No</SelectItem>
              <SelectItem value="true">Yes — mandatory in ranking formula</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="mt-4 flex justify-end gap-2">
        <Button data-testid={`ta-reject-${tournament.id}`} onClick={reject} variant="outline" className="rounded-sm uppercase tracking-widest text-xs font-bold">
          <XCircle className="w-4 h-4 mr-2" /> Not sanction
        </Button>
        <Button data-testid={`ta-approve-${tournament.id}`} onClick={approve} className="bg-jpa-primary hover:bg-jpa-primary-hover text-white rounded-sm uppercase tracking-widest text-xs font-bold">
          <CheckCircle2 className="w-4 h-4 mr-2" /> Approve & sanction
        </Button>
      </div>
    </div>
  );
}

export default function AdminApprovalsPanel({ onChange }) {
  const [pendingPlayers, setPendingPlayers] = useState([]);
  const [pendingClubs, setPendingClubs] = useState([]);
  const [pendingTournaments, setPendingTournaments] = useState([]);
  const load = () => {
    Promise.all([
      api.get("/admin/players/pending"),
      api.get("/admin/clubs/pending"),
      api.get("/admin/tournaments/pending"),
    ])
      .then(([p, c, t]) => { setPendingPlayers(p.data); setPendingClubs(c.data); setPendingTournaments(t.data); })
      .catch(() => {});
  };
  useEffect(() => { load(); }, []);
  const done = () => { load(); onChange?.(); };

  return (
    <div data-testid="admin-approvals-panel" className="space-y-8">
      <div>
        <div className="flex items-center gap-3 mb-4">
          <User className="w-5 h-5 text-jpa-primary" />
          <h3 className="font-display text-2xl tracking-wider">Player approvals</h3>
          <Badge className="bg-jpa-primary text-white rounded-sm">{pendingPlayers.length}</Badge>
        </div>
        {pendingPlayers.length === 0 ? (
          <div className="border border-dashed border-jpa-border rounded-sm p-10 text-center text-jpa-muted">
            <Clock className="w-6 h-6 mx-auto mb-2" /> No pending player registrations.
          </div>
        ) : (
          <div className="space-y-3">{pendingPlayers.map((p) => <PlayerApprovalCard key={p.id} player={p} onDone={done} />)}</div>
        )}
      </div>

      <div>
        <div className="flex items-center gap-3 mb-4">
          <Building2 className="w-5 h-5 text-jpa-primary" />
          <h3 className="font-display text-2xl tracking-wider">Club approvals</h3>
          <Badge className="bg-jpa-primary text-white rounded-sm">{pendingClubs.length}</Badge>
        </div>
        {pendingClubs.length === 0 ? (
          <div className="border border-dashed border-jpa-border rounded-sm p-10 text-center text-jpa-muted">
            <Clock className="w-6 h-6 mx-auto mb-2" /> No pending club registrations.
          </div>
        ) : (
          <div className="space-y-3">{pendingClubs.map((c) => <ClubApprovalCard key={c.id} club={c} onDone={done} />)}</div>
        )}
      </div>

      <div>
        <div className="flex items-center gap-3 mb-4">
          <Trophy className="w-5 h-5 text-jpa-primary" />
          <h3 className="font-display text-2xl tracking-wider">Tournament approvals</h3>
          <Badge className="bg-jpa-primary text-white rounded-sm">{pendingTournaments.length}</Badge>
        </div>
        {pendingTournaments.length === 0 ? (
          <div className="border border-dashed border-jpa-border rounded-sm p-10 text-center text-jpa-muted">
            <Clock className="w-6 h-6 mx-auto mb-2" /> No pending tournament submissions.
          </div>
        ) : (
          <div className="space-y-3">{pendingTournaments.map((t) => <TournamentApprovalCard key={t.id} tournament={t} onDone={done} />)}</div>
        )}
      </div>
    </div>
  );
}
